import * as Constants from '../../utilities/constants/Constants';

export class Placeable {
  constructor(data) {
    this.id = data.id;
    this.name = data.name;
    this.flippableX = data.flippable_x;
    this.width = Object.prototype.hasOwnProperty.call(data, 'image_width')
      ? data.image_width
      : data.width;
    this.height = Object.prototype.hasOwnProperty.call(data, 'image_height')
      ? data.image_height
      : data.height;
    this.imagePath = data.image_path;
    this.thumbnailWidth = data.thumbnail_width;
    this.thumbnailHeight = data.thumbnail_height;
    this.thumbnailPath = data.thumbnail_path;
    this.imageNonCdnUrl = data.image_non_cdn_url;
    this.printableAssetFreeForSamples = data.printable_asset_free_for_samples;
    this.printableImageUrl = data.printable_image_url;
    this.isSvgImage = data.is_svg_image;
    this.svgColors = data.svg_colors;
    this.colors = data.colors;
    this.rotatable = data.rotatable;
    this.themeId = data.theme_id;
    this.themeName = data.theme_name;
    this.externalPlaceable = false;

    if (Object.prototype.hasOwnProperty.call(data, 'printable_image_path')) {
      this.printableImagePath = data.printable_image_path;
    }

    // In case of Unsplash width is considerd as printable width
    this.printableImageWidth = Object.prototype.hasOwnProperty.call(
      data,
      'printable_image_width'
    )
      ? data.printable_image_width
      : data.width;
    this.printableImageHeight = Object.prototype.hasOwnProperty.call(
      data,
      'printable_image_height'
    )
      ? data.printable_image_height
      : data.height;

    this.croppedImagePath = null;
  }
}

export const tieleValue = placeable => {
  if (placeable) {
    if (Object.prototype.hasOwnProperty.call(placeable, 'isTiled')) {
      return placeable.isTiled;
    }
    if (Object.prototype.hasOwnProperty.call(placeable, 'tile')) {
      return placeable.tile;
    }
  }
  return false;
};

export class SolidColor {
  constructor(data) {
    this.color = data;
    this.type = Constants.SOLID_FILL_KEY;
  }
}

export class Scene extends Placeable {
  constructor(data) {
    super(data);
    this.tile = data.tile;
    this.showInfo = data.show_info;
    this.externalId = data.externalId;
    this.mediaId = data.media_id;
    this.type = Constants.SCENE_KEY;
  }
}

export class Element extends Placeable {
  constructor(data) {
    super(data);
    this.type = data.type;
    this.scalable = data.scalable;
    this.isPhoto = data.is_photo;
    this.mediaId = data.media_id;
    this.type = Constants.ELEMENT_KEY;
  }
}

export class Text extends Placeable {
  constructor(data) {
    super(data);
    this.type = data.type;
    this.text = data.text;
    this.rgb = data.rgb;
    this.textAnchor = data.text_anchor;
    this.fontId = data.font_id;
    this.textPath = data.text_path;
    this.shadow = data.shadow;
    this.shadowColor = data.shadow_color;
    this.imagePath = getTextImagePath(data.id, data.text_image_path);
  }
}

function getTextImagePath(textId, textImagePath) {
  if (textImagePath) {
    return textImagePath;
  }
  const previewPah = `/content/texts/${textId}/preview.png`;
  let parameter = `?${Date.now()}`;
  const questionMarkCharExists = previewPah && previewPah.indexOf('?') >= 0;
  if (questionMarkCharExists) {
    parameter = `&${Date.now()}`;
  }
  return previewPah + parameter;
}

export class UserPhoto extends Placeable {
  constructor(data) {
    super(data);

    // Few premium contents will have id in response as media_id and few as id.. Since user photo is created yet we need to send them
    // in media_id. But for uploaded content user photo is created in backend hence set id as placeable id.
    if (data.media_id) {
      this.mediaId = data.media_id;
    } else {
      this.mediaId = data.id;
      if (
        data.image_source === Constants.LOCAL_IMAGE_SOURCE ||
        data.image_source === Constants.AI_IMAGE_SOURCE
      ) {
        this.id = data.id;
      } else {
        this.id = null;
      }
    }
    this.userPhotoType = data.user_photo_type;
    this.imageSource = data.image_source;
    if (Object.prototype.hasOwnProperty.call(data, 'is_tiled')) {
      this.isTiled = data.is_tiled;
    }
    this.isPhoto = data.is_photo;
    this.costValue = data.cost_value;
    this.costCurrency = data.cost_currency;
    this.isSvgRecolored = data.is_svg_recolored;
    this.isSvgImageRecoloredInSession = data.is_svg_image_recolored_in_session;
    this.svgReferenceId = data.svg_reference_id;
    this.type = Constants.USER_PHOTO_TYPE;
    if (
      Object.prototype.hasOwnProperty.call(data, 'printable_image_content_type')
    ) {
      this.printableImageContentType = data.printable_image_content_type;
    }

    let contentAttributes = data;
    if (data.image_source == Constants.PATTERN_DESIGN_IMAGE_SOURCE) {
      if (
        Object.prototype.hasOwnProperty.call(data, 'pattern_design_attributes')
      ) {
        const attributes = JSON.parse(data.pattern_design_attributes);
        if (attributes.patterns.length > 0) {
          [contentAttributes] = attributes.patterns;
        }
      }
      initiatePatternDesignAttributes(this, contentAttributes);
    } else if (data.image_source == Constants.UNSPLASH_IMAGE_SOURCE) {
      if (Object.prototype.hasOwnProperty.call(data, 'unsplash_attributes')) {
        contentAttributes = JSON.parse(data.unsplash_attributes);
      }
      initiateUnsplashAttributes(this, contentAttributes);
    } else if (data.image_source == Constants.DREAMSTIME_IMAGE_SOURCE) {
      if (
        Object.prototype.hasOwnProperty.call(
          data,
          'dreamstime_images_attributes'
        )
      ) {
        const attributes = JSON.parse(data.dreamstime_images_attributes);
        contentAttributes = attributes.Image;
      }
      initiateDreamstimeAttributes(this, contentAttributes);
    } else if (data.image_source == Constants.GETTY_IMAGE_SOURCE) {
      if (
        Object.prototype.hasOwnProperty.call(data, 'getty_images_attributes')
      ) {
        contentAttributes = JSON.parse(data.getty_images_attributes);
      }
      initiateGettyImagesAttributes(this, contentAttributes);
    } else if (data.image_source == Constants.I_STOCK_IMAGE_SOURCE) {
      if (Object.prototype.hasOwnProperty.call(data, 'istock_attributes')) {
        contentAttributes = JSON.parse(data.istock_attributes);
      }
      initiateIstockAttributes(this, contentAttributes);
    } else if (data.image_source == Constants.ADOBE_STOCK_IMAGE_SOURCE) {
      if (
        Object.prototype.hasOwnProperty.call(data, 'adobe_stock_attributes')
      ) {
        contentAttributes = JSON.parse(data.adobe_stock_attributes);
      }
      initiateAdobeStockAttributes(this, contentAttributes);
    } else if (
      data.image_source == Constants.INSTAGRAM_IMAGE_SOURCE &&
      !Object.prototype.hasOwnProperty.call(data, 'user_photo_type')
    ) {
      initiateInstagramAttributes(this, contentAttributes);
    } else if (data.image_source == Constants.HP_MOSAIC_IMAGE_SOURCE) {
      if (
        Object.prototype.hasOwnProperty.call(data, 'hosted_content_attributes')
      ) {
        contentAttributes = JSON.parse(data.hosted_content_attributes);
      }
      initiateMosaicAttributes(this, contentAttributes);
    } else if (data.image_source == Constants.HOSTED_CONTENT_IMAGE_SOURCE) {
      if (
        Object.prototype.hasOwnProperty.call(data, 'hosted_content_attributes')
      ) {
        contentAttributes = JSON.parse(data.hosted_content_attributes);
      }
      initiateHostedContentAttributes(this, contentAttributes);
    }
  }
}

const setUserAttributes = userData => {
  const userAttributes = {
    name: userData.name,
    firstName: userData.first_name,
    lastName: userData.last_name,
    location: userData.location,
    profileImage: userData.profile_image,
    profileLink: userData.profile_link
  };
  return userAttributes;
};

export const initiatePatternDesignAttributes = (object, data) => {
  object.imageSource = Constants.PATTERN_DESIGN_IMAGE_SOURCE;
  object.userPhotoType = Constants.USER_PHOTO_SCENE_TYPE;

  object.thumbnailPath = data.thumbnails.gallery.url;
  object.imagePath = data.thumbnails.xlargeDiscreet.url;
  object.width = data.thumbnails.xlargeDiscreet.width;
  object.height = data.thumbnails.xlargeDiscreet.height;
  object.patternDesigner = data.designer;
  object.keywords = data.keywords;
  object.description = data.description;
  object.title = data.title;
  object.detailsUrl = data.detailsUrl;
  object.metadata = data.metadata;
  object.tile = true;
};

export const initiateUnsplashAttributes = (object, data) => {
  object.imageSource = Constants.UNSPLASH_IMAGE_SOURCE;

  if (Object.prototype.hasOwnProperty.call(data, 'image_url')) {
    object.thumbnailPath = data.image_url.thumb;
    object.imagePath = data.image_url.regular;
    object.user = setUserAttributes(data.user);
  }
};

export const initiateDreamstimeAttributes = (object, data) => {
  object.imageSource = Constants.DREAMSTIME_IMAGE_SOURCE;

  object.mediaId = data.ImageId;
  object.thumbnailPath = data.ThumbnailUrl;
  object.thumbnailWidth = data.Width;
  object.thumbnailHeight = data.Height;

  object.title = data.Title;
  object.detailsUrl = data.DetailUrl;
  object.accountType = null;
  object.themeId = data.ImageId;

  object.imagePath = data.PreviewUrl;
  const cropDimensions = data.PreviewCrop;
  data.width = cropDimensions.Width;
  data.height = cropDimensions.Height;

  const crop = {
    x: cropDimensions.Left,
    y: cropDimensions.Top,
    width: cropDimensions.Width,
    height: cropDimensions.Height
  };
  object.crop = crop;
};

export const initiateGettyImagesAttributes = (object, data) => {
  object.imageSource = Constants.GETTY_IMAGE_SOURCE;
  initiateGettyOrIStockAttributes(object, data);
};

export const initiateIstockAttributes = (object, data) => {
  object.imageSource = Constants.I_STOCK_IMAGE_SOURCE;
  initiateGettyOrIStockAttributes(object, data);
};

export const initiateAdobeStockAttributes = (object, data) => {
  object.imageSource = Constants.ADOBE_STOCK_IMAGE_SOURCE;

  const keyword = [];
  const tempKeywords = data.keywords;
  for (let i = 0; i < tempKeywords.length; i++) {
    keyword.push(tempKeywords[i].name);
  }
  object.thumbnailPath = data.thumbnail_240_url;
  object.thumbnailWidth = data.thumbnail_240_width;
  object.thumbnailHeight = data.thumbnail_240_height;
  object.imagePath = data.thumbnail_500_url;
  object.width = data.thumbnail_500_width;
  object.height = data.thumbnail_500_height;

  object.title = data.title;
  object.keywords = keyword.join(', ');
  object.author = data.creator_name;
  object.thumbnailHtmlTag = data.thumbnail_html_tag;
  object.detailsUrl = `${data.details_url}&as_campaign=HP`;
  object.views = data.nb_views;
  object.downloads = data.nb_downloads;
};

export const initiateMosaicAttributes = (object, data) => {
  initiateHostedContentAttributes(object, data);
  object.imageSource = Constants.HP_MOSAIC_IMAGE_SOURCE;
};

export const initiateHostedContentAttributes = (object, data) => {
  object.imageSource = Constants.HOSTED_CONTENT_IMAGE_SOURCE;

  object.imagePath = data.image_path;
  object.thumbnailPath = data.thumbnail_path;
  object.thumbnailWidth = data.image_width;
  object.thumbnailHeight = data.image_height;
  object.printableImagePath = data.printable_image_path;
  object.printableImageWidth = data.printable_image_width;
  object.printableImageHeight = data.printable_image_height;

  object.etag = data.etag;
  object.name = data.name;
  object.svgColors = data.svg_colors;
  object.tile = data.tile;
  object.isPhoto = data.is_photo;
  if (data.content_type === Constants.ELEMENT_KEY) {
    object.userPhotoType = Constants.USER_PHOTO_ELEMENT_TYPE;
  } else if (data.content_type == Constants.SCENE_KEY) {
    object.userPhotoType = Constants.USER_PHOTO_SCENE_TYPE;
  }
};

export const initiateGettyOrIStockAttributes = (object, data) => {
  const keyword = [];
  const tempKeywords = data.keywords;
  for (let i = 0; i < tempKeywords.length; i++) {
    keyword.push(tempKeywords[i].text);
  }

  const images = data.display_sizes.filter(size => size.name === 'preview');
  if (images.length > 0) {
    object.imagePath = images[0].uri;
    object.svgUrl = images[0].uri;
  }
  const thumbImages = data.display_sizes.filter(size => size.name === 'thumb');
  if (thumbImages.length > 0) {
    object.thumbnailPath = thumbImages[0].uri;
    object.thumbnailWidth = thumbImages[0].width;
    object.thumbnailHeight = thumbImages[0].height;
  }

  object.title = data.title;
  object.keywords = keyword.join(', ');
  object.artist = data.artist;
  object.viewUrl = data.referral_destinations[0].uri;

  const image = new Image();
  const url = object.imagePath.split('?')[0];
  image.src = url.concat('?s=2048x2048');
  image.onload = function () {
    object.width = this.width;
    object.height = this.height;
  };
};

export const initiateInstagramAttributes = (object, data) => {
  object.mediaType = data.media_type;
  object.imagePath = data.media_url;
  object.userPhotoType = Constants.USER_PHOTO_ELEMENT_TYPE;
  object.thumbnailPath = data.media_url;
  const image = new Image();
  image.src = object.imagePath;
  image.onload = function () {
    object.width = this.width;
    object.height = this.height;
    object.printableImageWidth = this.width;
    object.printableImageHeight = this.height;
  };
};
